<script setup>
import { computed } from 'vue';

import NextButton from 'dashboard/components-next/button/Button.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

import {
  formatChannelType,
  getAgentOptions,
  getSelectedAgentLabel,
  playgroundContent,
} from '../../helpers/kraftchatAIPlaygroundConfig';

const props = defineProps({
  agents: {
    type: Array,
    default: () => [],
  },
  selectedAgentId: {
    type: String,
    default: '',
  },
  question: {
    type: String,
    default: '',
  },
  isTesting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'submit',
  'update:selectedAgentId',
  'update:question',
]);

const agentOptions = computed(() => getAgentOptions(props.agents));

const selectedAgent = computed(() => {
  return props.agents.find(agent => String(agent.id) === props.selectedAgentId);
});

const selectedAgentLabel = computed(() =>
  getSelectedAgentLabel(props.agents, props.selectedAgentId)
);

const selectedAgentContext = computed(() => {
  if (!selectedAgent.value) return '';

  return [
    selectedAgent.value.inbox_name,
    formatChannelType(selectedAgent.value.channel_type),
  ].join(playgroundContent.agentContextSeparator);
});

const canSubmit = computed(() => {
  return Boolean(
    props.selectedAgentId && props.question.trim() && !props.isTesting
  );
});

const updateSelectedAgent = value => {
  emit('update:selectedAgentId', value);
};

const updateQuestion = value => {
  emit('update:question', value);
};
</script>

<template>
  <form
    class="rounded-xl border border-n-weak bg-n-background p-5"
    @submit.prevent="emit('submit')"
  >
    <div
      class="mb-5 flex flex-col gap-4 md:flex-row md:items-start md:justify-between"
    >
      <div class="max-w-2xl space-y-1">
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ playgroundContent.questionTitle }}
        </h3>

        <p class="text-sm leading-6 text-n-slate-11">
          {{ playgroundContent.questionDescription }}
        </p>
      </div>

      <div class="flex w-full flex-col gap-1 md:w-80">
        <label class="mb-0.5 text-heading-3 text-n-slate-12">
          {{ playgroundContent.agentLabel }}
        </label>

        <SelectMenu
          :model-value="selectedAgentId"
          class="!w-full [&>button]:!h-10 [&>button]:!w-full [&>button]:!max-w-none [&>button]:!justify-between [&>button]:!px-3 [&>div]:!w-full [&>div]:!max-w-none"
          :label="selectedAgentLabel"
          :options="agentOptions"
          sub-menu-position="bottom"
          @update:model-value="updateSelectedAgent"
        />

        <p
          v-if="selectedAgentContext"
          class="mt-1 truncate text-xs text-n-slate-10"
        >
          {{ selectedAgentContext }}
        </p>
      </div>
    </div>

    <TextArea
      :model-value="question"
      :label="playgroundContent.questionLabel"
      :placeholder="playgroundContent.questionPlaceholder"
      :max-length="1200"
      show-character-count
      auto-height
      min-height="3rem"
      max-height="12rem"
      @update:model-value="updateQuestion"
    />

    <div class="mt-5 flex justify-end">
      <NextButton
        type="submit"
        ruby
        :disabled="!canSubmit"
        :is-loading="isTesting"
        :label="
          isTesting
            ? playgroundContent.testingLabel
            : playgroundContent.submitLabel
        "
      />
    </div>
  </form>
</template>
